import { useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { fmtDay, qty, type WorkOrderRecord } from "@/types/production";
import { PriorityBadge, WorkOrderStatusBadge } from "./ProductionBadges";

const FILTERS = [
  { key: "OPEN", label: "Open", status: "PLANNED,IN_PROGRESS", late: false },
  { key: "LATE", label: "Running late", status: "", late: true },
  { key: "DONE", label: "Completed", status: "COMPLETED,CLOSED", late: false },
  { key: "CANCELLED", label: "Cancelled", status: "CANCELLED", late: false },
  { key: "ALL", label: "All", status: "", late: false },
];

export function WorkOrderList({ reloadKey }: { reloadKey: number }) {
  const { error } = useToast();
  const [search, setSearch] = useUrlSearch();
  const [filter, setFilter] = useState("OPEN");
  const list = usePagedList<WorkOrderRecord>({
    deps: [search, filter, reloadKey],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const qs = new URLSearchParams({ page: String(page), page_size: "30" });
      if (search.trim()) qs.set("search", search.trim());
      const f = FILTERS.find((x) => x.key === filter)!;
      if (f.status) qs.set("status", f.status);
      if (f.late) qs.set("late", "true");
      try {
        return unwrapPage<WorkOrderRecord>(await api.get(`/api/WorkOrder/List?${qs}`));
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load work orders");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none z-10" />
          <Input placeholder="Search by number, product, buyer or order…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 h-9 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${filter === f.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={6} />
      ) : (
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Work order</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.map((w) => (
                <TableRow key={w.id} className="hover:bg-muted/40">
                  <TableCell>
                    <Link to={`/production/${w.id}`} className="font-mono text-sm font-semibold text-primary hover:underline">{w.wo_number}</Link>
                    <div className="mt-0.5"><PriorityBadge priority={w.priority} /></div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{w.product_name}</div>
                    <div className="text-[11px] text-muted-foreground">{w.variant_name ? `${w.variant_name} · ` : ""}{qty(w.planned_qty, w.unit)}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {w.order ? (
                      <>
                        <Link to={`/orders/${w.order.id}`} className="text-primary hover:underline">{w.order.order_number}</Link>
                        <div className="text-[11px] text-muted-foreground">{w.account?.name}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">Stock</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="h-1.5 w-28 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: `${w.stages_total ? Math.round(((w.stages_done || 0) / w.stages_total) * 100) : 0}%` }} />
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">{w.current_stage_name ? `${w.current_stage_name} · ` : ""}{w.stages_done}/{w.stages_total} stages</div>
                  </TableCell>
                  <TableCell className={`text-sm whitespace-nowrap ${w.late ? "text-rose-600 font-semibold" : ""}`}>
                    {fmtDay(w.due_date)}
                    {w.late && <div className="text-[11px]">Running late</div>}
                  </TableCell>
                  <TableCell>
                    <WorkOrderStatusBadge status={w.work_order_status} />
                  </TableCell>
                </TableRow>
              ))}
              {list.rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground text-sm">No work orders here.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}
    </div>
  );
}
